// The Capital — a separate, walkable low-poly city the hero can visit.
// Art: Synty "POLYGON Samurai" demo village, converted by scripts/ (see
// docs/CAPITAL.md): capital_pieces.glb holds each unique mesh ONCE (meters, Y-up),
// capital_layout.json says where all ~1,600 placed copies sit (instanced at runtime)
// and carries precomputed collision footprints. Palette / night-glow are textures.
export const CapitalConfig = Object.freeze({
  dir: '/models/capital',
  piecesFile: 'capital_pieces.glb',
  layoutFile: 'capital_layout.json',
  navFile: 'capital_nav.bin',               // walkable-floor map (stairs, bridges, hills…)
  palette: 'PolygonSamurai_Tex_01.png',       // swap for _02/_03/_04 for other colour palettes
  emission: 'PolygonSamurai_Emmision_Night.png', // lanterns/windows glow after dark

  brightness: 1.95,   // the game's gentle lighting dims flat-colour palettes; lift them (still follows day/night)
  scale: 0.24,        // world units per metre of source art (a ~5 m house ≈ 1.75 world units)
  groundLevelM: 0.2,  // source-art height (m) of the street surface → sits at the hero's ground height
  chunkM: 60,         // metres per spatial chunk (so off-screen parts of the city are culled)
  walkMarginM: 10,    // how far past the outermost town building the hero may roam
  heroRadiusM: 0.45,  // collision padding around the hero, in source metres
  heroClearM: 0.9,    // headroom the hero needs (it's ~1 m tall) — it can duck under low things
  npcClearM: 2.1,     // headroom a warrior needs
  travelMinMs: 1500,  // never flash the travel screen faster than this

  // Wandering Samurai Warriors (proportion test). Real-world metres: a warrior is ~2.06 m tall.
  npcCount: 6,        // set 0 to turn the crowd off
  npcScale: 1,        // 1 = true size relative to the houses; try 0.8 / 1.2 to compare
  npcSpeed: 1.5,      // metres per second (matches the walk cycle's stride)
})

// Trip rules — what can happen at HOME while the hero is away in the Capital.
// Raids are REAL: the roll emits Events.RaidStart and the existing goon simulation spawns raiders and
// torches tiles. Every alert the player sees is built from those real events (see capitalTrip.js).
// (Plain object, not frozen, so dev tools / future tuning can adjust it live.)
export const TripConfig = {
  graceMs: 90_000,         // nothing can attack home for the first 90 s away
  riskBasePerMin: 0.30,    // chance of a raid per minute once the grace period is over…
  riskGrowPerMin: 0.20,    // …growing by this much for every extra minute away
  riskCapPerMin: 0.85,
  villageMult: 0.4,        // workers guarding the village (VILLAGE mode) cut the risk to 40%
  raidCooldownMs: 150_000, // gap between raids
  raidDurationMs: 30_000,  // how long a raid lasts if the player doesn't come home (same as adventure raids)
  maxRaids: 3,             // per trip
}

// Capital deals (rewards). Every faucet is capped per trip AND on a real-time cooldown (economy
// constitution: no faucet without a cap). Prices live here, not in code.
export const DealConfig = {
  reach: 7,                        // metres from a landmark to use it
  cooldownMs: { rice: 10 * 60_000, stipend: 15 * 60_000, pouch: 20 * 60_000 },
  rice: { priceMult: 1.25, capUnits: 40 },          // × RiceValue per unit, units per visit
  stipend: { minHonor: 6, perPoint: 150, cap: 750 },// (honor − 5) × perPoint, capped
  pouch: { spots: ['garden_bridge', 'moon_shrine', 'lotus_pond', 'north_torii', 'lantern_temple', 'lion_gate'],
           perTrip: 5, wealthFrac: 0.005, min: 40, max: 300 },
}

// Threats in the Capital. Thieves only target coin you've actually earned THIS trip (never banked wealth),
// can be outrun by sprinting, and are capped per trip.
export const ThiefConfig = {
  minNet: 200,          // a cutpurse only stalks you once your trip ledger is at least this far ahead
  firstDelayMs: 25_000, // after the ledger qualifies
  cooldownMs: 75_000,   // between thieves
  maxPerTrip: 2,
  spawnMin: 22, spawnMax: 36, // metres from the hero when one appears
  speed: 4.2,           // m/s — hero sprint is faster, so running escapes
  catchM: 1.1,
  stealFrac: 0.20, stealMin: 25, stealCap: 400,
  giveUpDist: 55, giveUpMs: 12_000, chaseMaxMs: 40_000, fleeMs: 5_000,
}
// The palace courtyard is closed to the public: pay the gate fee, or be fined once if you're caught inside.
export const PalaceConfig = {
  admission: 150,
  warnMs: 8_000,        // grace after the guards first spot you without leave
  fine: 200,
}

// Exploration leads — faint rings and stepping-stone dots on the ground that point the way to real, undiscovered landmarks
// (see docs/CAPITAL-LEADS.md). A lead only ever marks something that exists: its destination is a landmark in capital_pois.json
// and its dots follow a real walkable route baked by scripts/capital/bake_trails.mjs (capital_trails.json).
//   from      the landmark you must already have discovered for this lead to appear ('market' = where you arrive)
//   to        the landmark it points at (the lead disappears once you have found it)
//   admitted  also needs the palace gate fee paid on this trip (the courtyard is closed to the public)
export const LeadConfig = {
  chain: [
    // first steps — visible as soon as you arrive
    { from: 'market',         to: 'lantern_temple' },
    { from: 'market',         to: 'lotus_pond' },
    { from: 'market',         to: 'lion_gate' },
    // second ring — each appears only once you have found the landmark before it
    { from: 'lotus_pond',     to: 'moon_shrine' },
    { from: 'lantern_temple', to: 'north_torii' },
    { from: 'lantern_temple', to: 'palace_gate' },
    // inside the courtyard — only once the gate fee is paid
    { from: 'palace_gate',    to: 'garden_bridge', admitted: true },
    { from: 'palace_gate',    to: 'audience',      admitted: true },
  ],
  spacingM: 10,        // a stepping-stone dot every ~10 m of the route
  dotSenseM: 36,       // a dot shows only when you are within this distance (so the city is never littered with glows)
  ringSenseM: 80,      // a destination ring is first noticed from this far
  ringNearM: 22,       // …and is at its brightest inside this distance
  ringRadiusM: 2.6,
  dotRadiusM: 0.55,
  maxOpacity: 0.5,     // always faint: these are hints, not signs
}
